import axios from 'axios';
import { mergeFunctionKeyHeaders } from './functionKey';
import { abortableDelay } from './abortableDelay';

// Create a custom Axios instance with default configuration
const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  // No static timeout - we use progressive timeouts via AbortController in the request interceptor
});

// Retry configuration
const MAX_RETRIES = 5;
const RETRY_STATUS_CODES = [408, 429, 500, 502, 503, 504]; // Status codes that should trigger retry
const RETRY_DELAY_MS = 1000; // Base delay between retries in milliseconds
const BASE_TIMEOUT_MS = 10000; // Base timeout for first attempt (10 seconds)
const MAX_TIMEOUT_MS = 45000; // Hard cap for any single attempt
let activeRequestCount = 0;

// Progressive timeout calculation: increases with each retry attempt
// Attempt 1: 10s, Attempt 2: 20s, Attempt 3: 30s, Attempt 4: 40s, Attempt 5: 45s (capped)
const getTimeoutForAttempt = (retryCount) => {
  const timeoutMultipliers = [1, 2, 3, 4, 6]; // Multipliers for each attempt
  const multiplier = timeoutMultipliers[retryCount] || 6;
  return Math.min(BASE_TIMEOUT_MS * multiplier, MAX_TIMEOUT_MS);
};

const cleanupRequestLifecycle = (config) => {
  if (!config || config._requestLifecycleClosed) {
    return;
  }

  config._requestLifecycleClosed = true;

  if (config.timeoutId) {
    clearTimeout(config.timeoutId);
    config.timeoutId = null;
  }

  if (typeof config._removeExternalAbortListener === 'function') {
    config._removeExternalAbortListener();
    config._removeExternalAbortListener = null;
  }

  if (!config._activeRequestCountDecremented) {
    activeRequestCount = Math.max(0, activeRequestCount - 1);
    config._activeRequestCountDecremented = true;
  }
};

// Request interceptor for adding authentication or other headers
axiosInstance.interceptors.request.use(
  (config) => {
    // Attach retry count to config if not already present
    if (config.retryCount === undefined) {
      config.retryCount = 0;
    }
    
    // Track request lifecycle state up-front so all exit paths can clean up safely.
    config._requestLifecycleClosed = false;
    config._activeRequestCountDecremented = false;
    config._timeoutAbortFired = false;
    config._externalAbortFired = false;

    // Calculate progressive timeout based on retry attempt, then add a small
    // concurrency buffer so queued startup bursts don't hit false 5s-style aborts.
    activeRequestCount += 1;
    const configuredTimeout = Number(config._requestTimeoutMs);
    const attemptTimeout = Number.isFinite(configuredTimeout) && configuredTimeout > 0
      ? Math.min(configuredTimeout, MAX_TIMEOUT_MS)
      : getTimeoutForAttempt(config.retryCount);
    const concurrencyBufferMs = Math.min(Math.max(activeRequestCount - 1, 0) * 1000, 10000);
    const currentTimeout = Math.min(attemptTimeout + concurrencyBufferMs, MAX_TIMEOUT_MS);
    
    // Always create our own AbortController so we can attach a progressive
    // timeout regardless of whether a signal was already provided (e.g. from
    // RTK Query's baseQuery).  If an external signal exists we forward its
    // abort event so that both RTK Query cancellation AND our timeout can
    // independently abort the request.
    const controller = new AbortController();
    const existingSignal = config.signal;
    config._callerSignal = existingSignal;
    if (existingSignal) {
      if (existingSignal.aborted) {
        // External signal already fired – abort immediately.
        config._externalAbortFired = true;
        controller.abort(existingSignal.reason);
      } else {
        const onExternalAbort = () => {
          config._externalAbortFired = true;
          controller.abort(existingSignal.reason);
        };
        existingSignal.addEventListener(
          'abort',
          onExternalAbort,
          { once: true }
        );
        config._removeExternalAbortListener = () => {
          existingSignal.removeEventListener('abort', onExternalAbort);
        };
      }
    }
    config.abortController = controller;
    config.signal = controller.signal;

    // Set up automatic cancellation with progressive timeout.
    // This timeout only applies until we start receiving data.
    const timeoutId = setTimeout(() => {
      config._timeoutAbortFired = true;
      controller.abort(`Request timeout after ${currentTimeout / 1000} seconds (attempt ${config.retryCount + 1})`);
    }, currentTimeout);

    // Store timeout ID for cleanup
    config.timeoutId = timeoutId;
    
    // Add download progress handler to clear timeout once data starts flowing
    // This prevents aborting slow but active downloads
    const originalOnDownloadProgress = config.onDownloadProgress;
    config.onDownloadProgress = (progressEvent) => {
      // Clear the timeout once we start receiving data (response headers received)
      if (config.timeoutId) {
        clearTimeout(config.timeoutId);
        config.timeoutId = null;
      }
      
      // Call original handler if it exists
      if (originalOnDownloadProgress) {
        originalOnDownloadProgress(progressEvent);
      }
    };
    
    // You can modify the request config here before it's sent
    // For example, add auth token from localStorage if available
    config.headers = mergeFunctionKeyHeaders(config.headers || {}, config.url, config.baseURL);
    
    // Log outgoing requests in development environment
    if (process.env.NODE_ENV === 'development') {
      const requestSource = config.headers?.['x-client-source'] || config.headers?.['X-Client-Source'];
      console.log('Request:', config.method.toUpperCase(), config.url, `(timeout: ${currentTimeout / 1000}s, attempt: ${config.retryCount + 1}${requestSource ? `, source: ${requestSource}` : ''})`);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling and retry logic
axiosInstance.interceptors.response.use(
  (response) => {
    // Clean up timeout/listeners/lifecycle when request succeeds.
    cleanupRequestLifecycle(response.config);
    
    // You can modify the response data here before it's returned to the caller
    
    // Log successful responses in development environment
    if (process.env.NODE_ENV === 'development') {
      console.log('Response:', response.status, response.config.url);
    }
    
    return response;
  },
  async (error) => {
    const { config, response } = error;

    // Clean up timeout/listeners/lifecycle on error.
    cleanupRequestLifecycle(config);

    const lowerMessage = String(error?.message || '').toLowerCase();
    const isTimeoutAbort = Boolean(config?._timeoutAbortFired) ||
      error.code === 'ECONNABORTED' ||
      lowerMessage.includes('timeout') ||
      lowerMessage.includes('request timeout');
    const isExternalCancellation = Boolean(config?._externalAbortFired) ||
      (error.code === 'ERR_CANCELED' && !isTimeoutAbort);
    const isCancelled = isTimeoutAbort || isExternalCancellation || error.name === 'AbortError';
    
    // Only retry if config exists, and we haven't exceeded MAX_RETRIES
    // Retry on network errors, timeout/cancellation, or retryable status codes
    if (
      config && 
      config.retryCount < MAX_RETRIES && 
      config._retryEnabled !== false &&
      !isExternalCancellation &&
      (isTimeoutAbort || !response || RETRY_STATUS_CODES.includes(response?.status))
    ) {
      config.retryCount++;
      
      // Calculate exponential backoff delay
      const delay = RETRY_DELAY_MS * (2 ** (config.retryCount - 1));
      
      // Calculate next timeout for logging
      const nextTimeout = getTimeoutForAttempt(config.retryCount);
      
      // Log retry attempt
      const reason = isTimeoutAbort
        ? 'timeout'
        : `status ${response?.status || 'network error'}`;
      console.log(`Retrying request to ${config.url} due to ${reason} (attempt ${config.retryCount}/${MAX_RETRIES}) after ${delay}ms with ${nextTimeout / 1000}s timeout`);
      
      // Clean up previous AbortController and timeout
      if (config.abortController) {
        config.abortController = null;
      }
      config.timeoutId = null;
      config._removeExternalAbortListener = null;
      config._requestLifecycleClosed = false;
      config._activeRequestCountDecremented = false;
      config._timeoutAbortFired = false;
      config._externalAbortFired = false;
      config.signal = config._callerSignal;
      
      // Wait for the calculated delay
      await abortableDelay(delay, config._callerSignal);
      
      // Retry the request (new AbortController will be created in request interceptor)
      return axiosInstance(config);
    }
    
    // Handle common errors globally
    if (error.response) {
      // Server responded with an error status (4xx, 5xx)
      const status = error.response.status;
      
      if (status === 401) {
        // Unauthorized - handle authentication issues
        console.error('Authentication error. Please log in again.');
        // You might dispatch a Redux action to redirect to login page
      } else if (status === 403) {
        // Forbidden - handle permission issues
        console.error('Permission denied. You do not have access to this resource.');
      } else if (status === 404) {
        // Not found
        console.error('Resource not found.');
      } else if (status >= 500) {
        // Server error
        console.error('Server error. Please try again later.');
      }
      
      // Log error details in development environment
      if (process.env.NODE_ENV === 'development') {
        console.error('API Error:', status, error.response.data);
        if (config.retryCount > 0) {
          console.error(`Request failed after ${config.retryCount} retries`);
        }
      }
    } else if (error.request || isCancelled) {
      // Request was made but no response received (network issues) or request was cancelled/timed out
      const errorType = isExternalCancellation
        ? 'Request cancelled'
        : isTimeoutAbort
          ? 'Request timeout'
          : 'Network error';
      console.error(`${errorType}. Please check your connection.`);
      
      if (process.env.NODE_ENV === 'development') {
        console.error(`${errorType}:`, error.request || error.message);
        if (config && config.retryCount > 0) {
          console.error(`Request failed after ${config.retryCount} retries`);
        }
      }
    } else {
      // Error in setting up the request
      console.error('Error:', error.message);
    }
    
    // Return the error for the calling code to handle specifically
    return Promise.reject(error);
  }
);

export default axiosInstance;
